import React, { useEffect, useRef } from 'react';
import Bubble from './Bubble';
import MiniBubble from './MiniBubble';

// Convert lat/lng to pixel coordinates with proper center offset
const latLngToPixel = (lat, lng, map, mapContainer) => {
  if (!map || !mapContainer) return { x: 0, y: 0 }
  
  try {
    const bounds = map.getBounds()
    const center = map.getCenter()
    const zoom = map.getZoom()
    
    if (!bounds || !center) return { x: 0, y: 0 }
    
    const mapDiv = map.getDiv()
    const mapRect = mapDiv.getBoundingClientRect()
    
    // Calculate degrees per pixel at this zoom level
    const scale = Math.pow(2, zoom)
    const worldWidth = 256 * scale
    const worldHeight = 256 * scale
    
    // Convert to world coordinates (0-256 at zoom 0)
    const worldX = (lng + 180) * (worldWidth / 360)
    const worldY = (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * worldHeight
    
    // Convert center to world coordinates
    const centerWorldX = (center.lng() + 180) * (worldWidth / 360)
    const centerWorldY = (1 - Math.log(Math.tan(center.lat() * Math.PI / 180) + 1 / Math.cos(center.lat() * Math.PI / 180)) / Math.PI) / 2 * worldHeight
    
    // Calculate pixel offset from center
    const pixelX = (worldX - centerWorldX) + (mapRect.width / 2)
    const pixelY = (worldY - centerWorldY) + (mapRect.height / 2)
    
    return { x: pixelX, y: pixelY }
  } catch (error) {
    return { x: 0, y: 0 }
  }
}

const UnitsOverlay = ({ 
  units, 
  map, 
  mapContainer,
  selectedUnit,
  onUnitSelect,
  stateData,
  setActiveUnit,
  onContextMenu,
  onSessionVideos,
  onOpenUnit,
  useMiniMap = false
}) => {
  const overlayRef = useRef(null)
  const unitsRef = useRef({})
  
  // Update unit positions immediately when map changes
  const updateUnitPositions = () => {
    if (!map || !mapContainer || !overlayRef.current) return
    
    units
      .filter(unit => unit.last_pos_latitude && unit.last_pos_longitude)
      .forEach(unit => {
        const unitElement = unitsRef.current[unit.unit_id]
        if (unitElement) {
          const { x, y } = latLngToPixel(
            unit.last_pos_latitude, 
            unit.last_pos_longitude, 
            map, 
            mapContainer
          )
          unitElement.style.left = `${x - 15}px`
          unitElement.style.top = `${y - 15}px`
        }
      })
  }
  
  // Listen for map events directly
  useEffect(() => {
    if (!map) return
    
    const listeners = [
      map.addListener('center_changed', () => requestAnimationFrame(updateUnitPositions)),
      map.addListener('zoom_changed', () => requestAnimationFrame(updateUnitPositions)),
      map.addListener('bounds_changed', () => requestAnimationFrame(updateUnitPositions))
    ]
    
    return () => listeners.forEach(listener => listener.remove())
  }, [map, units, mapContainer])
  
  // Initial render and when units change
  useEffect(() => {
    updateUnitPositions()
  }, [units, map, mapContainer])
  
  return (
    <div 
      ref={overlayRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
    >
      {units
        .filter(unit => unit.last_pos_latitude && unit.last_pos_longitude)
        .map(unit => {
          const { x, y } = latLngToPixel(
            unit.last_pos_latitude, 
            unit.last_pos_longitude, 
            map, 
            mapContainer
          )
          
          return (
            <div
              key={unit.unit_id}
              ref={el => unitsRef.current[unit.unit_id] = el}
              style={{
                position: 'absolute',
                left: x - 15,
                top: y - 15,
                pointerEvents: 'auto',
                zIndex: selectedUnit === unit.unit_id ? 1000 : 100
              }}
            >
              {useMiniMap ? (
                <MiniBubble 
                  unit={unit}
                  onUnitSelect={onUnitSelect}
                  isSelected={selectedUnit === unit.unit_id}
                />
              ) : (
                <Bubble 
                  onContextMenu={onContextMenu}
                  unit={unit}
                  setActiveUnit={setActiveUnit}
                  stateData={stateData[unit.unit_id]}
                  onUnitSelect={onUnitSelect}
                  isSelected={selectedUnit === unit.unit_id}
                  onSessionVideos={onSessionVideos}
                  onOpenUnit={onOpenUnit}
                />
              )}
            </div>
          )
        })
      }
    </div>
  )
}

export default UnitsOverlay